import type {
  DraftOrdersListResponseType,
  DraftOrdersListParametersType,
} from "@sellar-io/api-v2/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

export const useDraftOrderList = <TData = DraftOrdersListResponseType,>(
  params: DraftOrdersListParametersType & { limit?: number },
  options?: { enabled: boolean },
) => {
  const maxPerPage = params.limit ?? 20;
  const supplierIdList = params.query[0]?.value ?? [];

  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useInfiniteQuery({
    queryKey: [user?.uid, business?.id, ServerStateKeys.Orders, params],
    queryFn: ({ pageParam }) => {
      const start = pageParam;
      const end = Math.min(pageParam + maxPerPage, supplierIdList.length);
      const idsToFetch = supplierIdList.slice(start, end);
      return apiV2Request({
        user,
        business,
        method: "get",
        path: `draft-orders`,
        params: {
          query: [
            {
              type: "supplierIdList",
              modifier: "IN",
              value: idsToFetch,
            },
          ],
        },
      }).then((data) => apiV2AssertResponse<TData>(data).data);
    },

    getNextPageParam: (_lastPage, pages) => {
      const nextIndex = pages.length * maxPerPage;
      return nextIndex < supplierIdList.length ? nextIndex : undefined;
    },
    initialPageParam: 0,
    enabled: options?.enabled,
  });
};
